import type { ApiResponse } from "@tabipla/shared/types";
import type { ApiClientInterface } from "@tabipla/hooks/infrastructure";
import { getErrorCodeFromStatus } from "@tabipla/shared";

const API_BASE_URL = import.meta.env.VITE_API_URL || "";

class ApiClient implements ApiClientInterface {
  private baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<ApiResponse<T>> {
    const normalizedEndpoint = endpoint.startsWith("/") ? endpoint : `/${endpoint}`;
    const url = `${this.baseUrl}${normalizedEndpoint}`;

    const response = await fetch(url, {
      ...options,
      headers: {
        "Content-Type": "application/json",
        ...options.headers,
      },
      credentials: "include",
    });

    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({}));
      // Backend returns: { success: false, error: { code: "...", message: "..." } }
      const errorData = errorBody.error || {};
      return {
        success: false,
        error: {
          code: errorData.code || getErrorCodeFromStatus(response.status),
          message: errorData.message || errorBody.error || response.statusText,
        },
      };
    }

    const data = await response.json();
    return { success: true, data: data.data ?? data };
  }

  async get<T>(endpoint: string): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: "GET" });
  }

  async post<T>(endpoint: string, body?: unknown): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: "POST",
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  async put<T>(endpoint: string, body?: unknown): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: "PUT",
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  async delete<T>(endpoint: string): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: "DELETE" });
  }

  async patch<T>(endpoint: string, body?: unknown): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: "PATCH",
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  async postForm<T>(endpoint: string, formData: FormData): Promise<ApiResponse<T>> {
    const normalizedEndpoint = endpoint.startsWith("/") ? endpoint : `/${endpoint}`;
    const url = `${this.baseUrl}${normalizedEndpoint}`;

    const response = await fetch(url, {
      method: "POST",
      body: formData,
      credentials: "include",
      // Note: Don't set Content-Type header for FormData, browser sets it automatically with boundary
    });

    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({}));
      const errorData = errorBody.error || {};
      return {
        success: false,
        error: {
          code: errorData.code || getErrorCodeFromStatus(response.status),
          message: errorData.message || errorBody.error || response.statusText,
        },
      };
    }

    const data = await response.json();
    return { success: true, data: data.data ?? data };
  }

  // Alias for ApiClientInterface compatibility
  async uploadFormData<T>(endpoint: string, formData: FormData): Promise<ApiResponse<T>> {
    return this.postForm<T>(endpoint, formData);
  }
}

export const apiClient = new ApiClient(API_BASE_URL);
